import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Truck, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function Embarques() {
  const [tab, setTab] = useState('nuevo');
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [tarimasEnBodega, setTarimasEnBodega] = useState([]);
  const [selectedFolios, setSelectedFolios] = useState([]);
  const [responsable, setResponsable] = useState('');
  const [placas, setPlacas] = useState('');
  const [destino, setDestino] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [embarques, setEmbarques] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/catalogs/warehouses').then(res => setWarehouses(res.data));
    API.get('/embarques').then(res => setEmbarques(res.data));
  }, []);

  useEffect(() => {
    if (warehouseId) {
      API.get('/tarimas/en-bodega/lista', { params: { warehouse_id: warehouseId } })
        .then(res => setTarimasEnBodega(res.data));
    }
  }, [warehouseId]);

  const toggleFolio = (folio) => {
    setSelectedFolios(prev => 
      prev.includes(folio) ? prev.filter(f => f !== folio) : [...prev, folio]
    );
  };

  const handleSend = async () => {
    if (selectedFolios.length === 0) { toast.error('Selecciona al menos una tarima'); return; }
    if (!window.confirm(`¿Confirmar salida de ${selectedFolios.length} tarima(s)?`)) return;
    setSaving(true);
    try {
      const res = await API.post('/embarques', {
        folios_tarima: selectedFolios,
        responsable, placas, destino, observaciones
      });
      toast.success(`Embarque ${res.data.folio_salida} registrado`);
      setSelectedFolios([]); setResponsable(''); setPlacas(''); setDestino(''); setObservaciones('');
      // Refresh
      if (warehouseId) {
        const t = await API.get('/tarimas/en-bodega/lista', { params: { warehouse_id: warehouseId } });
        setTarimasEnBodega(t.data);
      }
      const e = await API.get('/embarques');
      setEmbarques(e.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const totalPiezas = tarimasEnBodega.filter(t => selectedFolios.includes(t.folio_tarima)).reduce((s, t) => s + t.cantidad_actual, 0);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Salida de Camión</h1>
        <p className="text-sm text-muted-foreground">Registrar salida de tarimas en camión</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="nuevo">Nueva Salida</TabsTrigger>
          <TabsTrigger value="historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="nuevo" className="mt-4 space-y-4">
          {/* Header */}
          <Card className="bg-white border">
            <CardHeader className="pb-3"><CardTitle className="text-base">Datos del Embarque</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-sm font-medium">Almacén *</label>
                  <Select value={warehouseId} onValueChange={setWarehouseId}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Responsable</label>
                  <Input value={responsable} onChange={(e) => setResponsable(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-medium">Placas</label>
                  <Input value={placas} onChange={(e) => setPlacas(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-medium">Destino</label>
                  <Input value={destino} onChange={(e) => setDestino(e.target.value)} className="mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pallet Selection */}
          <Card className="bg-white border">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Tarimas en Bodega</CardTitle>
              {selectedFolios.length > 0 && (
                <span className="text-sm font-semibold text-[#0F6E87]">{selectedFolios.length} seleccionadas | {totalPiezas} pzas</span>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-muted/30">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Folio</TableHead><TableHead>Grupo</TableHead>
                  <TableHead className="text-right">Piezas</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {tarimasEnBodega.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      {warehouseId ? 'Sin tarimas en bodega' : 'Selecciona un almacén'}
                    </TableCell></TableRow>
                  )}
                  {tarimasEnBodega.map(t => (
                    <TableRow key={t.folio_tarima} className={`hover:bg-muted/40 cursor-pointer ${selectedFolios.includes(t.folio_tarima) ? 'bg-[#0F6E87]/5' : ''}`}
                      onClick={() => toggleFolio(t.folio_tarima)}>
                      <TableCell>
                        <Checkbox checked={selectedFolios.includes(t.folio_tarima)} onCheckedChange={() => toggleFolio(t.folio_tarima)} />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{t.folio_tarima}</TableCell>
                      <TableCell>{t.group_code}</TableCell>
                      <TableCell className="text-right font-semibold">{t.cantidad_actual}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSend} disabled={saving || selectedFolios.length === 0} className="bg-[#0F6E87] hover:bg-[#0C5D73]">
              <Send className="h-4 w-4 mr-1" /> {saving ? 'Registrando...' : 'Registrar Salida'}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <Card className="bg-white border"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/30">
                <TableHead>Folio</TableHead><TableHead>Responsable</TableHead><TableHead>Placas</TableHead>
                <TableHead>Destino</TableHead><TableHead className="text-right">Tarimas</TableHead>
                <TableHead className="text-right">Piezas</TableHead><TableHead>Fecha</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {embarques.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin embarques</TableCell></TableRow>}
                {embarques.map(e => (
                  <TableRow key={e.id} className="hover:bg-muted/40">
                    <TableCell className="font-mono text-xs">{e.folio_salida}</TableCell>
                    <TableCell>{e.responsable}</TableCell>
                    <TableCell>{e.placas}</TableCell>
                    <TableCell>{e.destino}</TableCell>
                    <TableCell className="text-right">{e.lineas?.length || 0}</TableCell>
                    <TableCell className="text-right font-semibold">{e.lineas?.reduce((s, l) => s + l.cantidad_piezas, 0) || 0}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(e.fecha).toLocaleDateString('es-MX')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
